import Vue from "vue";
import { extend, localize } from "vee-validate";
import * as rules from 'vee-validate/dist/rules';
import es from "vee-validate/dist/locale/es.json";
import en from "vee-validate/dist/locale/en.json";
import { reglasPasaportesPorPais } from "./composables/front/common/validacionesPaises";
import i18n from '@/i18n';

Object.keys(rules).forEach(rule => {
    extend(rule, rules[rule]);
});

extend('confirmed_ci', {
    params: ['target'],
    validate(value, { target }) {
        return value.toLowerCase() == target.toLowerCase();
    },
    message: es.messages.confirmed,
});

extend('identificacion', {
    /**
     * @param {string} value
     * @param {{ tipo: string }} params
     */
    validate(value, { tipo }) {
        if (!tipo) return false;
        if (!value) return true;

        const expresiones = {
            dni: /^0?[0-9]{8}[A-Za-z]$/,
            nie: /^[XYZ]\d{7}[A-Z]$/,
            nif: /^[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J]$/,
            otros: /^[A-Za-z0-9]{2,20}$/,                      // Libre
        };

        const validChars = 'TRWAGMYFPDXBNJZSQVHLCKE';
        const str = value.toString().toUpperCase().replace(/\s/g, '');
        const tipoNormalizado = tipo.toLowerCase();

        const regex = expresiones[tipoNormalizado];

        if (!regex || !regex.test(str)) {
            return false;
        }

        // NIF/CIF y OTROS no usan la letra calculada por módulo 23 del DNI
        if (tipoNormalizado !== 'otros' && tipoNormalizado !== 'nif') {
            const dniLetter = str.slice(-1);
            let dniNumber = str
                .slice(0, -1)
                .replace('X', '0')
                .replace('Y', '1')
                .replace('Z', '2');
    
            const calculatedLetter = validChars.charAt(parseInt(dniNumber, 10) % 23);
    
            return calculatedLetter === dniLetter;
        }

        return true;
    },
    params: ['tipo'],
    message: (field, { tipo }) => {
        return `El ${tipo} introducido no es válido`;
    }
});

extend('valid_nif_cif', {
    validate(value) {
        if (!value) return true;

        const nif = value.toUpperCase().replace(/\s|-/g, '');

        // Solo valida CIF/NIF jurídico (letra + 7 dígitos + control).
        // DNI/NIE se validan con la regla `identificacion` cuando el tipo es DNI o NIE.
        const cifRegex = /^[ABCDEFGHJNPQRSUVW][0-9]{7}[0-9A-J]$/;

        if (!cifRegex.test(nif)) {
            return false;
        }

        const letraEntidad = nif[0];
        const digitos = nif.substring(1, 8);
        const control = nif[8];

        let sumaPares =
            parseInt(digitos[1], 10) +
            parseInt(digitos[3], 10) +
            parseInt(digitos[5], 10);

        let sumaImpares = 0;
        for (let i = 0; i < 7; i += 2) {
            let d = parseInt(digitos[i], 10) * 2;
            sumaImpares += d > 9 ? d - 9 : d;
        }

        const sumaTotal = sumaPares + sumaImpares;
        const unidades = sumaTotal % 10;
        const dCalculado = unidades === 0 ? 0 : 10 - unidades;
        const lCalculada = String.fromCharCode(64 + dCalculado);

        if ('ABEHJUV'.includes(letraEntidad)) {
            return control == dCalculado || 'Dígito de control incorrecto';
        }

        if ('NPQRSW'.includes(letraEntidad)) {
            return control == lCalculada || 'Letra de control incorrecta';
        }

        return (
            control == dCalculado ||
            control == lCalculada ||
            'Control incorrecto'
        );
    },
    message: 'El NIF/CIF introducido no es válido'
});

extend('pasaporte', {
    /**
     * Valida pasaportes según el país seleccionado
     * @param {string} value - Número de pasaporte
     * @param {object} args - { pais: 'ES' | 'US' | ... } (código ISO alpha-2)
     */
    params: ['pais'],
    validate(value, { pais }) {
        if (!value) return false;

        const codigoPais = pais?.toUpperCase();
        const regex = reglasPasaportesPorPais[codigoPais] || /^[A-Z0-9]{5,20}$/i;
        const normalizedValue = String(value).trim().replace(/\s+/g, '');
        return regex.test(normalizedValue);
    },
    message: (field, { pais }) => {
        if (pais) {
            return i18n.t('formularios.pasaporte_invalido_pais', { pais });
        }
        return i18n.t('formularios.pasaporte_invalido');
    }
});

extend('no_comas', {
    validate(value) {
        return !value.includes(',');
    },
    message: 'Utiliza punto como separador decimal, no comas.'
});

extend('dos_decimales', {
    validate(value) {
        return /^\d+(\.\d{1,2})?$/.test(value);
    },
    message: 'No puedes introducir más de dos decimales.'
});

extend('decimal', {
    validate(value) {
        return /^\d+(\.\d{1,2})?$/.test(String(value));
    },
    message: 'El importe debe ser un número decimal válido (ej: 10.50).'
});

extend('decimal_requerido', {
    validate(value) {
        return /^-?\d+(\.\d{1,2})?$/.test(String(value));
    },
    message: 'El importe debe ser un número válido (ej: 1, 1.5, -1.5).'
});

// Acepta enteros (2), un decimal (2.2) o dos decimales (2.22), admitiendo
// tanto punto como coma como separador decimal (locale ES). No admite
// negativos (algunos importes a sumar al monedero no tienen sentido en
// negativo — para los que SÍ lo permiten, usar decimal_monedero).
extend('decimal_opcional', {
    validate(value) {
        return /^\d+([.,]\d{1,2})?$/.test(String(value));
    },
    message: 'El importe no es válido (ej: 10, 10.5).'
});

extend('decimal_monedero', {
    validate(value) {
        return /^-?\d+\.\d{1,2}$/.test(String(value));
    },
    message: 'El importe debe tener decimales (ej: 10.50, -5.00).'
});

extend('nombrePlantilla', {
    validate: value => {
        const regex = /^[a-z0-9_]+$/;
        return regex.test(value);
    },
    message: 'Solo se permiten letras minúsculas, números y guiones bajos',
});

extend('url', {
    validate: value => {
        const regex = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/\S*)?$/i;
        return regex.test(value);
    },
    message: 'Debe ser una URL válida (ej: https://ejemplo.com)'
});

localize({
    en: {
        messages: en.messages,
        names: {
            sga_id: 'Evento SGA',
            lfp_id: 'Evento LFP',
            products: 'Productos asociados al evento'
        }
    },
    es: {
        messages: es.messages,
        names: {
            sga_id: 'Evento SGA',
            lfp_id: 'Evento LFP',
            products: 'Productos asociados al evento'
        }
    }
});

let LOCALE = "es";

localize(LOCALE);

// A simple get/set interface to manage our locale in components.
// This is not reactive, so don't create any computed properties/watchers off it.
Object.defineProperty(Vue.prototype, "locale", {
    get() {
        return LOCALE;
    },
    set(val) {
        LOCALE = val;
        localize(val);
    }
});

extend('valid_date', {
    validate: (value, args) => {
        if (!value) return true;

        const date = new Date(value);
        if (isNaN(date.getTime())) return false;

        const inputDate = new Date(date);
        inputDate.setHours(0, 0, 0, 0);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const year = inputDate.getFullYear();

        if (year < 1900) return false;

        const mode = args && args[0];

        if (mode === 'noFuture' && inputDate > today) return false;

        if (mode === 'noPast' && inputDate < today) return false;

        return true;
    },
    message: 'La fecha no es válida'
});
